import React from 'react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  FontFamily,
  FontWeight,
  Text,
} from '@gousto-internal/citrus-react'
import { type MenuRecipeSubtitleTag, type StandardRecipeItem } from '@library/api-menu-experience'

import { TileImage as RecipeTileImage } from '../../RecipeGrid/RecipeTileImage/TileImage'
import { RecipeDietaryTagOverlay, RecipeTagLabels } from '../../RecipeTileTags'
import {
  CardTextFlow,
  MerchandisingBasketButton,
  MerchandisingRecipeBannerTagStrip,
  MerchandisingRecipeSurcharge,
  type MerchandisingRecipeCardProps,
  twoLineTitleClampStyle,
} from './campaignBannerCard.atoms'

const getKeyMetadata = (subtitleTags: MenuRecipeSubtitleTag[] = []) => subtitleTags.slice(0, 2)

const MetadataRow = ({ subtitleTags }: { subtitleTags?: MenuRecipeSubtitleTag[] }) => {
  const metadata = getKeyMetadata(subtitleTags)

  if (metadata.length === 0) {
    return null
  }

  return (
    <Box
      data-testid="campaign-banner-metadata"
      display={Display.Flex}
      alignItems={AlignItems.Center}
      gap={1}
      wrap
      style={{ color: '#6D7B88' }}
    >
      {metadata.map((tag, index) => (
        <React.Fragment key={`${tag.type}-${tag.text}`}>
          {index > 0 ? (
            <Text aria-hidden="true" size={2} style={{ color: 'inherit' }}>
              •
            </Text>
          ) : null}
          <Text size={2} style={{ color: 'inherit' }}>
            {tag.text}
          </Text>
        </React.Fragment>
      ))}
    </Box>
  )
}

const onSpaceOrEnter =
  (callback: (event: React.KeyboardEvent) => void) => (event: React.KeyboardEvent) => {
    if (event.code === 'Space' || event.code === 'Enter') {
      event.preventDefault()
      callback(event)
    }
  }

const CAMPAIGN_TILE_WIDTH = 'min(82vw, 332px)'
const CAMPAIGN_TILE_HEIGHT = '352px'

export type CampaignBannerRecipeCardProps = MerchandisingRecipeCardProps<StandardRecipeItem>

export const CampaignBannerRecipeCard = ({
  recipe,
  onClick,
  onAddClick,
  onRemoveClick,
  onBlockedAddAttempt,
  canAddRecipes,
  reachedLimit,
  isInBasket,
  isOutOfStock,
  index,
  renderFlexPortionBadge,
  renderFlexPortionEditor,
}: CampaignBannerRecipeCardProps) => {
  const flexPortionBadge = renderFlexPortionBadge?.(recipe.id)
  const flexPortionEditor = renderFlexPortionEditor?.(recipe.id)
  const showFlexPortions = Boolean(flexPortionBadge || flexPortionEditor)
  const campaignBanner = recipe.tagGroups.campaignBannerFooter

  return (
    <Box
      data-testid="campaign-banner-recipe-card"
      data-testing={isOutOfStock ? 'menuRecipeOutOfStock' : 'menuRecipeViewDetails'}
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      flexShrink={0}
      onClick={() => onClick(recipe.id)}
      onKeyDown={onSpaceOrEnter(() => onClick(recipe.id))}
      role="button"
      tabIndex={0}
      style={{
        borderRadius: '4px',
        cursor: 'pointer',
        height: CAMPAIGN_TILE_HEIGHT,
        overflow: 'hidden',
        position: 'relative',
        width: CAMPAIGN_TILE_WIDTH,
      }}
      boxShadow="0 8px 10px -8px rgba(19, 35, 53, 0.18)"
    >
      <Box
        position={DisplayPosition.Relative}
        style={{
          borderRadius: '4px 4px 0 0',
          height: 'min(52vw, 196px)',
          overflow: 'hidden',
        }}
      >
        <RecipeTileImage
          lazy={false}
          recipe={{
            images: [
              {
                type: 'image',
                ...recipe.image,
              },
            ],
            name: recipe.title,
          }}
          insideOverflow
          testId="campaign-banner-recipe-card-image"
        />
        <RecipeTagLabels
          tags={recipe.tagGroups.general ?? []}
          reserveEndSpace
          dataTestId="campaign-banner-general-tags"
        />
        <RecipeDietaryTagOverlay
          tags={recipe.tagGroups.dietary ?? []}
          dataTestId="campaign-banner-dietary-tags"
        />
        {flexPortionBadge && (
          <Box
            display={Display.Flex}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            position={DisplayPosition.Absolute}
            style={{
              bottom: 16,
              left: 16,
              right: 16,
              justifyContent: 'flex-end',
              zIndex: 3,
            }}
          >
            {flexPortionBadge}
          </Box>
        )}
      </Box>
      <Box
        bg={Color.White}
        paddingH={3}
        paddingBottom={3}
        style={{
          flex: 1,
          marginTop: -2,
          minHeight: '124px',
          paddingTop: 0,
          position: 'relative',
        }}
      >
        {!showFlexPortions && (
          <MerchandisingBasketButton
            recipeId={recipe.id}
            isInBasket={isInBasket}
            isOutOfStock={isOutOfStock}
            canAddRecipes={canAddRecipes}
            reachedLimit={reachedLimit}
            onAddClick={onAddClick}
            onRemoveClick={onRemoveClick}
            onBlockedAddAttempt={onBlockedAddAttempt}
            index={index}
          />
        )}
        <CardTextFlow>
          <Box paddingV={5} paddingBottom={2}>
            <Text
              fontFamily={FontFamily.Book}
              fontWeight={FontWeight.SemiBold}
              size={3}
              style={twoLineTitleClampStyle}
            >
              {recipe.title}
            </Text>
          </Box>
        </CardTextFlow>
        <MetadataRow subtitleTags={recipe.tagGroups.subtitle} />
        <MerchandisingRecipeSurcharge pricing={recipe.pricing} isOutOfStock={isOutOfStock} />
      </Box>
      <MerchandisingRecipeBannerTagStrip banner={campaignBanner} />
      {flexPortionEditor}
    </Box>
  )
}
